import React, { useEffect, useState } from 'react'
import { Wallet } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNightMode } from '../../contexts/NightModeContext';
import { OrganizerProfile } from '../../types/Common';
import { getOrganizerProfile, stripeOnboarding, stripeAccountLink, getStripeAccountStatus } from '../../api';
import { StripeAccountStatusResponse } from '../../types/StripeAccountStatusResponse';
import Background from '../Login/Background';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import './Payout.css';

const Payout = () => {
    const { isNightMode } = useNightMode();
    const { user } = useAuth();
    document.title = "Payouts | EventoX";
    const [organizerDetails, setOrganizerDetails] = useState<OrganizerProfile | null>(null);
    const [stripeStatus, setStripeStatus] = useState<StripeAccountStatusResponse | null>(null);
    const [isActivating, setIsActivating] = useState(false);
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch organizer details and Stripe account status
    useEffect(() => {
        if (user) {
            const fetchData = async () => {
                try {
                    // Fetch organizer details
                    const organizerDetailsData = await getOrganizerProfile();
                    console.log("Organizer Details Data:", organizerDetailsData);
                    setOrganizerDetails(organizerDetailsData);

                    // If Stripe account exists, check its status
                    if (organizerDetailsData?.stripeAccountId) {
                        setIsLoadingStatus(true);
                        try {
                            const statusData: any = await getStripeAccountStatus();
                            console.log("Stripe Account Status:", statusData);
                            setStripeStatus(statusData);
                        } catch (statusError) {
                            console.error("Error fetching Stripe status:", statusError);
                            // Don't set error here, just log it
                        } finally {
                            setIsLoadingStatus(false);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching organizer details:", error);
                    setError("Failed to load organizer details");
                }
            }
            fetchData();
        }
    }, [user]);

    // Handle Stripe activation
    const handleActivateStripe = async () => {
        setIsActivating(true);
        setError(null);
        
        try {
            // Step 1: Create Stripe account (onboarding)
            const onboardingResponse: { accountId?: string } = await stripeOnboarding();
            console.log("Stripe onboarding response:", onboardingResponse);
            
            if (!onboardingResponse?.accountId) {
                throw new Error("Failed to create Stripe account");
            }

            // Step 2: Get account link for onboarding
            const accountLinkResponse: { link?: string } = await stripeAccountLink(onboardingResponse.accountId);
            console.log("Stripe account link response:", accountLinkResponse);
            
            if (!accountLinkResponse?.link) {
                throw new Error("Failed to get Stripe onboarding link");
            }

            // Step 3: Redirect user to Stripe onboarding page
            window.location.href = accountLinkResponse.link;
            
        } catch (error: any) {
            console.error("Error activating Stripe:", error);
            setError(error?.response?.data?.message || error?.message || "Failed to activate Stripe. Please try again.");
            setIsActivating(false);
        }
    };

    // Determine Stripe account status
    const getAccountStatus = () => {
        if (!organizerDetails?.stripeAccountId) {
            return 'not_setup';
        }
        if (isLoadingStatus || !stripeStatus) {
            return 'checking';
        }
        const requirements = (stripeStatus as any).requirements;
        if (requirements?.currently_due?.length === 0 && stripeStatus.account.capabilities.transfers === 'active') {
            return 'fully_active';
        }
        if (requirements?.currently_due?.length > 0 || stripeStatus.account.capabilities.transfers === 'active') {
            return 'partially_active';
        }
        return 'incomplete';
    };

    const accountStatus = getAccountStatus();

    // Render status card based on account status
    const renderStatusCard = () => {
        switch (accountStatus) {
            case 'not_setup':
                return {
                    icon: 'fa-solid fa-circle-exclamation',
                    title: 'Stripe Connect Not Set Up',
                    message: 'You need to set up your Stripe Connect account to receive payouts from ticket sales.',
                    tone: 'warn',
                    showAction: true
                };
            case 'checking':
                return {
                    icon: 'fa-solid fa-spinner fa-spin',
                    title: 'Checking Account Status',
                    message: 'Please wait while we verify your Stripe account status...',
                    tone: 'info',
                    showAction: false
                };
            case 'fully_active':
                return {
                    icon: 'fa-solid fa-circle-check',
                    title: 'Stripe Connect Fully Active',
                    message: 'Your account is fully set up and ready to accept payments. You can now sell tickets and receive payouts.',
                    tone: 'ok',
                    showAction: false
                };
            case 'partially_active':
                return {
                    icon: 'fa-solid fa-triangle-exclamation',
                    title: 'Account Setup Incomplete',
                    message: 'Your Stripe account is partially set up. Please complete the onboarding process to enable ticket sales.',
                    tone: 'warn',
                    showAction: true
                };
            case 'incomplete':
                return {
                    icon: 'fa-solid fa-clock',
                    title: 'Onboarding In Progress',
                    message: 'Your Stripe account setup is in progress. Please complete all required steps to enable payments.',
                    tone: 'warn',
                    showAction: true
                };
            default:
                return null;
        }
    };

    const statusCard = renderStatusCard();

    return (
        <div className={`contact-list-container payout-page ${isNightMode ? 'text-white' : 'text-dark'}`}>
            <Background />
            <PageHeader
                title="Payout Settings"
                subtitle="Connect Stripe to receive ticket revenue and review your account status."
                icon={Wallet}
            />
            
            {statusCard && (
                <div className={`glass-panel payout-status-card payout-status-${statusCard.tone}`}>
                    <div className="payout-status-icon">
                        <i className={statusCard.icon}></i>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 className="payout-status-title">{statusCard.title}</h2>
                        <p className="payout-status-message">{statusCard.message}</p>

                        {stripeStatus && accountStatus !== 'checking' && (
                            <div className="payout-status-meta payout-status-meta-warn">
                                {(() => {
                                    const currentlyDue = (stripeStatus as any).requirements?.currently_due?.length || 0;
                                    const transfersActive = stripeStatus.account.capabilities.transfers === 'active';
                                    return (
                                        <>
                                            <div className="payout-status-meta-item">
                                                <i className={`fa-solid ${currentlyDue === 0 ? 'fa-circle-check payout-ok' : 'fa-circle-xmark payout-bad'}`}></i>
                                                <span>Details submitted: {currentlyDue === 0 ? 'Yes' : 'No'}</span>
                                            </div>
                                            <div className="payout-status-meta-item">
                                                <i className={`fa-solid ${transfersActive ? 'fa-circle-check payout-ok' : 'fa-circle-xmark payout-bad'}`}></i>
                                                <span>Charges enabled: {transfersActive ? 'Yes' : 'No'}</span>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                        {statusCard.showAction && (
                            <div style={{ marginTop: 16 }}>
                                <button
                                    className="v3-btn-primary"
                                    onClick={handleActivateStripe}
                                    disabled={isActivating}
                                    style={{ opacity: isActivating ? 0.7 : 1 }}
                                >
                                    {isActivating ? (
                                        <>
                                            <i className="fa-solid fa-spinner fa-spin"></i>
                                            Activating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-link"></i>
                                            {accountStatus === 'not_setup' ? 'Set Up Stripe Connect' : 'Complete Setup'}
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {error && (
                            <div className="payout-error">
                                <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 8 }}></i>
                                {error}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Detailed Account Information */}
            {stripeStatus && accountStatus !== 'checking' && (
                <div>
                    <h2 className="payout-section-head">
                        <i className="fa-solid fa-circle-info" style={{ color: '#F79B59' }}></i>
                        Account details
                    </h2>

                    <div className="payout-detail-grid">
                        {/* Account Overview */}
                        <DetailCard
                            title="Account Overview"
                            icon="fa-solid fa-building"
                        >
                            <DetailRow label="Account ID" value={stripeStatus.account.id || 'N/A'} />
                            <DetailRow label="Object Type" value={stripeStatus.account.object} />
                            <DetailRow label="Business Type" value={stripeStatus.account.business_type || 'N/A'} />
                            <DetailRow 
                                label="Charges Enabled" 
                                value={stripeStatus.account.charges_enabled ? 'Yes' : 'No'}
                                tone={stripeStatus.account.charges_enabled ? 'ok' : 'bad'}
                            />
                            <DetailRow 
                                label="Payouts Enabled" 
                                value={(stripeStatus as any).payouts_enabled ? 'Yes' : 'No'}
                                tone={(stripeStatus as any).payouts_enabled ? 'ok' : 'bad'}
                            />
                            {/* <DetailRow label="Company Name" value={stripeStatus.account.company.name || 'N/A'} /> */}
                        </DetailCard>

                        {/* Capabilities */}
                        <DetailCard
                            title="Capabilities"
                            icon="fa-solid fa-check-circle"
                        >
                            <DetailRow 
                                label="Card Payments" 
                                value={stripeStatus.account.capabilities.card_payments || 'N/A'}
                                tone={stripeStatus.account.capabilities.card_payments === 'active' ? 'ok' : 'warn'}
                            />
                            <DetailRow 
                                label="Transfers" 
                                value={stripeStatus.account.capabilities.transfers || 'N/A'}
                                tone={stripeStatus.account.capabilities.transfers === 'active' ? 'ok' : 'warn'}
                            />
                        </DetailCard>

                        <DetailCard
                            title="Business Profile"
                            icon="fa-solid fa-briefcase"
                        >
                            <DetailRow label="Business Name" value={stripeStatus.account.business_profile.name || 'N/A'} />
                            <DetailRow label="URL" value={stripeStatus.account.business_profile.url || 'N/A'} />
                            <DetailRow label="MCC Code" value={stripeStatus.account.business_profile.mcc || 'N/A'} />
                            <DetailRow label="Support Email" value={stripeStatus.account.business_profile.support_email || 'N/A'} />
                            <DetailRow label="Support Phone" value={stripeStatus.account.business_profile.support_phone || 'N/A'} />
                            <DetailRow label="Support URL" value={stripeStatus.account.business_profile.support_url || 'N/A'} />
                            {stripeStatus.account.business_profile.annual_revenue && (
                                <DetailRow label="Annual Revenue" value={`$${stripeStatus.account.business_profile.annual_revenue.toLocaleString()}`} />
                            )}
                            {stripeStatus.account.business_profile.estimated_worker_count && (
                                <DetailRow label="Estimated Workers" value={stripeStatus.account.business_profile.estimated_worker_count.toString()} />
                            )}
                        </DetailCard>

                        {/* Requirements Status */}
                        {(() => {
                            const requirements = (stripeStatus as any).requirements;
                            const currentlyDue = requirements?.currently_due?.length || 0;
                            return (
                                <DetailCard
                                    title="Requirements Status"
                                    icon="fa-solid fa-clipboard-check"
                                >
                                    <DetailRow 
                                        label="Currently Due" 
                                        value={currentlyDue === 0 ? 'None' : `${currentlyDue} items`}
                                        tone={currentlyDue === 0 ? 'ok' : 'bad'}
                                    />
                                    {currentlyDue > 0 && requirements?.currently_due && (
                                        <div className="payout-note payout-note-warn">
                                            <strong style={{ display: 'block', marginBottom: 8 }}>Required items</strong>
                                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                                {requirements.currently_due.map((item: string, index: number) => (
                                                    <li key={index}>{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {requirements?.past_due && (
                                        <DetailRow 
                                            label="Past Due" 
                                            value={requirements.past_due.length === 0 ? 'None' : `${requirements.past_due.length} items`}
                                            tone={requirements.past_due.length === 0 ? 'ok' : 'bad'}
                                        />
                                    )}
                                    {requirements?.eventually_due && (
                                        <DetailRow 
                                            label="Eventually Due" 
                                            value={requirements.eventually_due.length === 0 ? 'None' : `${requirements.eventually_due.length} items`}
                                        />
                                    )}
                                    {requirements?.current_deadline && (
                                        <DetailRow label="Current Deadline" value={new Date(requirements.current_deadline).toLocaleDateString()} />
                                    )}
                                    {requirements?.disabled_reason && (
                                        <DetailRow label="Disabled Reason" value={requirements.disabled_reason} tone="bad" />
                                    )}
                                    {requirements?.alternatives && requirements.alternatives.length > 0 && (
                                        <DetailRow label="Alternatives" value={`${requirements.alternatives.length} alternatives available`} />
                                    )}
                                    {requirements?.errors && requirements.errors.length > 0 && (
                                        <div className="payout-note payout-note-bad">
                                            <strong style={{ display: 'block', marginBottom: 8 }}>Errors</strong>
                                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                                {requirements.errors.map((error: string, index: number) => (
                                                    <li key={index}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </DetailCard>
                            );
                        })()}

                        {/* Individual Information */}
                        {(stripeStatus as any).individual && (
                            <DetailCard
                                title="Individual Information"
                                icon="fa-solid fa-user"
                            >
                                <DetailRow label="Email" value={(stripeStatus as any).individual.email || 'N/A'} />
                                <DetailRow label="First Name" value={(stripeStatus as any).individual.first_name || 'N/A'} />
                                <DetailRow label="Last Name" value={(stripeStatus as any).individual.last_name || 'N/A'} />
                                {(stripeStatus as any).individual.relationship && (
                                    <>
                                        <DetailRow label="Owner" value={(stripeStatus as any).individual.relationship.owner ? 'Yes' : 'No'} />
                                        <DetailRow label="Director" value={(stripeStatus as any).individual.relationship.director ? 'Yes' : 'No'} />
                                        <DetailRow label="Executive" value={(stripeStatus as any).individual.relationship.executive ? 'Yes' : 'No'} />
                                        {(stripeStatus as any).individual.relationship.percent_ownership && (
                                            <DetailRow label="Ownership %" value={`${(stripeStatus as any).individual.relationship.percent_ownership}%`} />
                                        )}
                                        <DetailRow label="Title" value={(stripeStatus as any).individual.relationship.title || 'N/A'} />
                                    </>
                                )}
                            </DetailCard>
                        )}

                        {/* Controller Information */}
                        {stripeStatus.account.controller && (
                            <DetailCard
                                title="Controller Information"
                                icon="fa-solid fa-user-shield"
                            >
                                <DetailRow label="Is Controller" value={stripeStatus.account.controller.is_controller ? 'Yes' : 'No'} />
                                <DetailRow label="Fees Payer" value={stripeStatus.account.controller.fees.payer || 'N/A'} />
                                <DetailRow label="Losses Payments" value={stripeStatus.account.controller.losses.payments || 'N/A'} />
                                <DetailRow label="Account Holder Name" value={stripeStatus.account.controller.account_holder_name || 'N/A'} />
                                <DetailRow label="Dashboard Type" value={stripeStatus.account.controller.stripe_dashboard.type || 'N/A'} />
                                <DetailRow label="Requirement Collection" value={stripeStatus.account.controller.requirement_collection || 'N/A'} />
                                {stripeStatus.account.controller.last4 && (
                                    <DetailRow label="Last 4 Digits" value={stripeStatus.account.controller.last4} />
                                )}
                                {stripeStatus.account.controller.routing_number && (
                                    <DetailRow label="Routing Number" value={stripeStatus.account.controller.routing_number} />
                                )}
                                <DetailRow label="Status" value={stripeStatus.account.controller.status || 'N/A'} />
                            </DetailCard>
                        )}

                        {/* Payment Settings */}
                        {(stripeStatus as any).settings && (
                            <DetailCard
                                title="Payment Settings"
                                icon="fa-solid fa-credit-card"
                            >
                                {(stripeStatus as any).settings.card_payments && (
                                    <>
                                        <DetailRow label="Display Name" value={(stripeStatus as any).settings.card_payments.dashboard.display_name || 'N/A'} />
                                        <DetailRow label="Timezone" value={(stripeStatus as any).settings.card_payments.dashboard.timezone || 'N/A'} />
                                        {(stripeStatus as any).settings.card_payments.payments && (
                                            <>
                                                <DetailRow label="Statement Descriptor" value={(stripeStatus as any).settings.card_payments.payments.statement_descriptor || 'N/A'} />
                                                {(stripeStatus as any).settings.card_payments.payouts && (
                                                    <>
                                                        <DetailRow 
                                                            label="Debit Negative Balances" 
                                                            value={(stripeStatus as any).settings.card_payments.payouts.debit_negative_balances ? 'Yes' : 'No'} 
                                                        />
                                                        {(stripeStatus as any).settings.card_payments.payouts.schedule && (
                                                            <>
                                                                <DetailRow label="Payout Delay Days" value={(stripeStatus as any).settings.card_payments.payouts.schedule.delay_days?.toString() || 'N/A'} />
                                                                <DetailRow label="Payout Interval" value={(stripeStatus as any).settings.card_payments.payouts.schedule.interval || 'N/A'} />
                                                            </>
                                                        )}
                                                        <DetailRow label="Payout Statement Descriptor" value={(stripeStatus as any).settings.card_payments.payouts.statement_descriptor || 'N/A'} />
                                                    </>
                                                )}
                                            </>
                                        )}
                                        {(stripeStatus as any).settings.card_payments.decline_on && (
                                            <>
                                                <DetailRow label="Decline on AVS Failure" value={(stripeStatus as any).settings.card_payments.decline_on.avs_failure ? 'Yes' : 'No'} />
                                                <DetailRow label="Decline on CVC Failure" value={(stripeStatus as any).settings.card_payments.decline_on.cvc_failure ? 'Yes' : 'No'} />
                                            </>
                                        )}
                                    </>
                                )}
                            </DetailCard>
                        )}

                        {/* Branding Settings */}
                        {(stripeStatus as any).settings?.branding && (
                            <DetailCard
                                title="Branding"
                                icon="fa-solid fa-palette"
                            >
                                <DetailRow label="Icon" value={(stripeStatus as any).settings.branding.icon ? 'Set' : 'Not Set'} />
                                <DetailRow label="Logo" value={(stripeStatus as any).settings.branding.logo ? 'Set' : 'Not Set'} />
                                <DetailRow label="Primary Color" value={(stripeStatus as any).settings.branding.primary_color || 'N/A'} />
                                <DetailRow label="Secondary Color" value={(stripeStatus as any).settings.branding.secondary_color || 'N/A'} />
                            </DetailCard>
                        )}

                        {/* Terms of Service */}
                        {(stripeStatus as any).settings?.tos_acceptance && (
                            <DetailCard
                                title="Terms of Service"
                                icon="fa-solid fa-file-contract"
                            >
                                <DetailRow 
                                    label="Accepted Date" 
                                    value={(stripeStatus as any).settings.tos_acceptance.date ? new Date((stripeStatus as any).settings.tos_acceptance.date * 1000).toLocaleDateString() : 'N/A'} 
                                />
                            </DetailCard>
                        )}

                        {/* Metadata */}
                        {(stripeStatus as any).metadata && Object.keys((stripeStatus as any).metadata).length > 0 && (
                            <DetailCard
                                title="Metadata"
                                icon="fa-solid fa-tags"
                            >
                                {Object.entries((stripeStatus as any).metadata).map(([key, value]) => (
                                    <DetailRow key={key} label={key} value={String(value)} />
                                ))}
                            </DetailCard>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

// Helper component for detail rows
const DetailRow = ({ label, value, tone }: { label: string; value: string | number | null | undefined; tone?: 'ok' | 'warn' | 'bad' }) => {
    if (value === null || value === undefined) return null;
    
    return (
        <div className="payout-detail-row">
            <span className="payout-detail-label">{label}</span>
            <span className={`payout-detail-value ${tone ? `payout-${tone}` : ''}`}>
                {String(value)}
            </span>
        </div>
    );
};

const DetailCard = ({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) => {
    return (
        <div className="glass-panel payout-detail-card">
            <h3 className="v3-section-head">
                <i className={icon} style={{ color: '#F79B59' }}></i>
                {title}
            </h3>
            <div>
                {children}
            </div>
        </div>
    );
};

export default Payout;